import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

class CartDrawerComponent extends Component {
  #timer = null;
  #timerKey = 'modr_cart_session_end';
  #globalSubmitHandler = null;
  #globalClickHandler = null;

  get #drawer() { return this.closest('theme-drawer'); }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#drawer?.addEventListener(DrawerOpenEvent.eventName, this.#onOpen);
    this.#installGlobalAddToCartHandler();
    this.#installCheckoutHandler();
    this.#enhance();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#drawer?.removeEventListener(DrawerOpenEvent.eventName, this.#onOpen);
    if (this.#globalSubmitHandler) document.removeEventListener('submit', this.#globalSubmitHandler, true);
    if (this.#globalClickHandler) document.removeEventListener('click', this.#globalClickHandler, true);
    this.#stopTimer();
  }

  #onOpen = () => this.#enhance();

  #onCartUpdate = (event) => {
    event.promise?.then(() => requestAnimationFrame(() => this.#enhance())).catch(() => {});
  };

  #installGlobalAddToCartHandler() {
    if (this.#globalSubmitHandler) return;
    this.#globalSubmitHandler = (event) => {
      const form = event.target instanceof HTMLFormElement ? event.target : event.target?.closest?.('form');
      if (!form || !form.action || !/\/cart\/add(?:\.js)?(?:[?#]|$)/.test(form.action)) return;
      if (form.dataset.modrCartHandled === '1') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      form.dataset.modrCartHandled = '1';
      this.#ajaxAdd(form).finally(() => { delete form.dataset.modrCartHandled; });
    };
    document.addEventListener('submit', this.#globalSubmitHandler, true);
  }

  #installCheckoutHandler() {
    if (this.#globalClickHandler) return;
    this.#globalClickHandler = (event) => {
      const button = event.target?.closest?.('#cart-drawer #checkout, #cart-drawer .cart__checkout-button');
      if (!button || button.disabled) return;

      const formId = button.getAttribute('form');
      const form = formId ? document.getElementById(formId) : button.closest('form');
      if (!(form instanceof HTMLFormElement)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      try {
        form.requestSubmit(button);
      } catch (error) {
        console.error('[MODR Cart Drawer] Native checkout submit failed', error);
        const checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = 'Checkout';
        form.appendChild(checkoutInput);
        HTMLFormElement.prototype.submit.call(form);
      }
    };
    document.addEventListener('click', this.#globalClickHandler, true);
  }

  async #ajaxAdd(form) {
    const button = form.querySelector('button[type="submit"], input[type="submit"]');
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
    try {
      const root = window.Shopify?.routes?.root || '/';
      const action = root + 'cart/add.js';
      const response = await fetch(action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error('add_to_cart_failed');
      await response.json();

      const end = Date.now() + 600000;
      sessionStorage.setItem(this.#timerKey, String(end));
      localStorage.setItem(this.#timerKey, String(end));

      await this.#refreshDrawer();
      const drawer = document.getElementById('cart-drawer');
      if (drawer?.open) drawer.open();
    } catch (error) {
      console.error('[MODR Cart Drawer]', error);
      window.location.href = (window.Shopify?.routes?.root || '/') + 'cart';
    } finally {
      if (button) { button.disabled = false; button.removeAttribute('aria-busy'); }
    }
  }

  async #refreshDrawer() {
    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(root, window.location.origin);
    url.searchParams.set('sections', 'cart-drawer-section');
    const response = await fetch(url.toString(), {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('drawer_refresh_failed');
    const sections = await response.json();
    const html = sections['cart-drawer-section'];
    if (!html) throw new Error('drawer_section_missing');
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const incoming = doc.querySelector('#shopify-section-cart-drawer-section cart-drawer-component');
    const current = document.querySelector('#shopify-section-cart-drawer-section cart-drawer-component');
    if (!incoming || !current) throw new Error('drawer_markup_missing');
    current.innerHTML = incoming.innerHTML;
    const inner = current.querySelector('.cart-drawer__inner');
    if (inner?.classList.contains('cart-drawer--empty')) inner.classList.remove('cart-drawer--empty');
    this.#enhance();
  }

  #isEmpty() { return Boolean(this.querySelector('.cart-drawer--empty')); }

  #enhance() {
    if (this.#isEmpty()) { this.#stopTimer(); return; }
    const inner = this.querySelector('.cart-drawer__inner');
    if (!inner) return;
    this.#addStyles();
    this.#ensureTimerPosition(inner);
  }

  #ensureTimerPosition(inner) {
    this.querySelectorAll('.modr-cart-ship, .cart-totals__tax-note, [data-cart-tax-note], .tax-note').forEach((node) => node.remove());

    let timer = this.querySelector('.modr-cart-timer');
    if (!timer) {
      timer = document.createElement('div');
      timer.className = 'modr-cart-timer';
      timer.innerHTML = '<span class="modr-cart-timer-ic">⏳</span><span class="modr-cart-timer-text">Votre panier est réservé encore <b class="modr-cart-timer-clock">10:00</b></span>';
    }

    const itemsComponent = inner.querySelector('.cart-items-component');
    if (itemsComponent && timer.parentElement !== inner) {
      inner.insertBefore(timer, itemsComponent);
    } else if (timer.parentElement !== inner) {
      inner.prepend(timer);
    }

    this.#startTimer(timer);
  }

  #addStyles() {
    if (document.getElementById('modr-cart-drawer-style')) return;
    const style = document.createElement('style');
    style.id = 'modr-cart-drawer-style';
    style.textContent = `

          /* MODR — FIX QUANTITE CART DRAWER */
      #cart-drawer .cart-items__quantity {
        width: fit-content !important;
        max-width: 100% !important;
      }

      #cart-drawer .cart-items__quantity-controls {
        width: fit-content !important;
        display: flex !important;
        align-items: center !important;
        gap: 8px !important;
      }

      #cart-drawer .cart-items__quantity .quantity-selector {
        display: flex !important;
        flex: 0 0 108px !important;
        width: 108px !important;
        min-width: 108px !important;
        max-width: 108px !important;
        height: 42px !important;
        min-height: 42px !important;
        max-height: 42px !important;
        align-self: center !important;
        justify-content: space-between !important;
        align-items: center !important;
        overflow: hidden !important;
        border-radius: 999px !important;
      }

      #cart-drawer .cart-items__quantity .quantity-selector input[type="number"] {
        flex: 0 0 42px !important;
        width: 42px !important;
        min-width: 42px !important;
        max-width: 42px !important;
        height: 40px !important;
        min-height: 40px !important;
        padding: 0 !important;
        margin: 0 !important;
        border: 0 !important;
        outline: none !important;
        box-shadow: none !important;
        background: transparent !important;
        color: #ffffff !important;
        -webkit-text-fill-color: #ffffff !important;
        opacity: 1 !important;
        font-size: 15px !important;
        font-weight: 800 !important;
        line-height: 40px !important;
        text-align: center !important;
        appearance: textfield !important;
      }

      #cart-drawer .cart-items__quantity .quantity-selector .quantity-minus,
      #cart-drawer .cart-items__quantity .quantity-selector .quantity-plus {
        flex: 0 0 32px !important;
        width: 32px !important;
        min-width: 32px !important;
        max-width: 32px !important;
        height: 40px !important;
        min-height: 40px !important;
        padding: 0 !important;
        margin: 0 !important;
        color: #ffffff !important;
      }

      #cart-drawer .cart-items__quantity .quantity-selector .svg-wrapper,
      #cart-drawer .cart-items__quantity .quantity-selector svg {
        width: 14px !important;
        height: 14px !important;
      }

      @media (max-width: 749px) {
        #cart-drawer .cart-items__quantity .quantity-selector {
          flex-basis: 104px !important;
          width: 104px !important;
          min-width: 104px !important;
          max-width: 104px !important;
        }

        #cart-drawer .cart-items__quantity .quantity-selector input[type="number"] {
          flex-basis: 38px !important;
          width: 38px !important;
          min-width: 38px !important;
          max-width: 38px !important;
        }

        #cart-drawer .cart-items__quantity .quantity-selector .quantity-minus,
        #cart-drawer .cart-items__quantity .quantity-selector .quantity-plus {
          flex-basis: 31px !important;
          width: 31px !important;
          min-width: 31px !important;
          max-width: 31px !important;
        }
      }
      .color-custom-drawer{background:linear-gradient(180deg,#08080d,#0d0a16 55%,#09090f)!important;color:#f7f7fb!important;border-left:1px solid rgba(199,72,255,.28)!important;box-shadow:-20px 0 70px rgba(0,0,0,.58)!important}
      .color-custom-drawer .theme-drawer__header{background:rgba(8,8,13,.96)!important;border-bottom:1px solid rgba(255,255,255,.08)!important}
      .color-custom-drawer .theme-drawer__title{text-transform:uppercase!important;font-weight:900!important;letter-spacing:.04em!important;color:#fff!important}
      .color-custom-drawer .cart-drawer__content{display:flex!important;flex-direction:column!important;min-height:0!important;overflow-y:auto!important;overflow-x:hidden!important}
      .color-custom-drawer .cart-drawer__items{flex:0 0 auto!important;display:block!important;overflow:visible!important;min-height:0!important;padding-inline:16px!important}
      .color-custom-drawer .cart-items__wrapper{display:block!important;width:100%!important;visibility:visible!important;opacity:1!important}
      .color-custom-drawer .cart-items__table{display:table!important;width:100%!important;visibility:visible!important;opacity:1!important}
      .color-custom-drawer .cart-items__table-row{display:grid!important;visibility:visible!important;opacity:1!important;min-height:84px!important}
      .color-custom-drawer .cart-items__media{display:block!important;visibility:visible!important}
      .color-custom-drawer .cart-items__details{display:block!important;visibility:visible!important}
      .color-custom-drawer .cart-items__title{color:#fff!important;font-weight:800!important}
      .color-custom-drawer .cart-items__variants-wrapper,.color-custom-drawer .cart-items__variants{color:#aaa5bb!important;display:block!important;visibility:visible!important}
      .color-custom-drawer .cart-items__quantity{display:flex!important;visibility:visible!important}
      .color-custom-drawer .cart-items__price{display:block!important;visibility:visible!important}
      .color-custom-drawer .cart-drawer__summary{position:relative!important;bottom:auto!important;z-index:2!important;margin-top:0!important;padding:16px!important;background:#09090f!important;mask-image:none!important;border-top:1px solid rgba(255,255,255,.08)!important}
      .color-custom-drawer .modr-cart-timer{margin:0 12px 14px!important;border:1px solid rgba(255,61,145,.24)!important;border-radius:14px!important;background:linear-gradient(135deg,rgba(255,45,120,.08),rgba(111,66,255,.12))!important;padding:11px 13px!important;display:flex!important;align-items:center!important;gap:9px!important;color:#ddd8e8!important;flex:0 0 auto!important}
      .color-custom-drawer .modr-cart-timer-text{font-size:11px!important;font-weight:700!important}
      .color-custom-drawer .modr-cart-timer-clock{color:#ff2d92!important;font-variant-numeric:tabular-nums!important}
      .color-custom-drawer .modr-cart-ship,.color-custom-drawer .cart-totals__tax-note,.color-custom-drawer [data-cart-tax-note],.color-custom-drawer .tax-note{display:none!important}
      .color-custom-drawer .cart-drawer__summary button,.color-custom-drawer .cart-drawer__summary .button{border-radius:12px!important;min-height:50px!important;font-weight:900!important;text-transform:uppercase!important}
      .color-custom-drawer .cart-drawer__summary .button--primary,.color-custom-drawer .cart-drawer__summary button[type=submit]{background:linear-gradient(100deg,#ff2d78,#c33dff 55%,#5d5bff)!important;color:#fff!important;border:0!important;box-shadow:0 10px 30px rgba(211,49,255,.22)!important}
      @media(max-width:749px){.color-custom-drawer .theme-drawer__dialog{width:100vw!important;max-width:100vw!important}.color-custom-drawer .cart-drawer__items{padding-inline:12px!important}.color-custom-drawer .cart-drawer__summary{padding:13px!important}.color-custom-drawer .modr-cart-timer{margin-inline:12px!important}}
    `;
    document.head.appendChild(style);
  }

  #startTimer(timer) {
    if (!timer) return;
    let end = Number(sessionStorage.getItem(this.#timerKey) || localStorage.getItem(this.#timerKey) || 0);
    if (!end || end <= Date.now()) {
      end = Date.now() + 600000;
      sessionStorage.setItem(this.#timerKey, String(end));
      localStorage.setItem(this.#timerKey, String(end));
    }

    this.#stopTimer();
    const el = timer.querySelector('.modr-cart-timer-clock');
    if (!el) return;

    const tick = () => {
      const left = Math.max(0, end - Date.now());
      const seconds = Math.floor(left / 1000);
      el.textContent = `${String(Math.floor(seconds / 60)).padStart(2,'0')}:${String(seconds % 60).padStart(2,'0')}`;
      timer.classList.toggle('is-ending', seconds <= 60 && seconds > 0);
      if (!left) this.#stopTimer();
    };

    tick();
    this.#timer = window.setInterval(tick, 1000);
  }

  #stopTimer() {
    if (this.#timer) { clearInterval(this.#timer); this.#timer = null; }
  }
}

if (!customElements.get('cart-drawer-component')) customElements.define('cart-drawer-component', CartDrawerComponent);
